import type { ComponentType, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Activity, ArrowRightLeft, NotebookPen, Server, ShieldCheck, Trophy, UserCog, Users, UsersRound } from 'lucide-react';
import type { StaffPerms } from '../../utils/staffPermissions';
import { canAccessDataController } from '../../utils/dataControllerPermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useCountUp } from '../../hooks/useCountUp';
import { cn } from '../../utils/cn';

export type StaffHubTabId = 'roster' | 'teams' | 'tickets' | 'notes' | 'activity' | 'migrations' | 'provisioning' | 'data-controller';

interface HubTab {
  id: StaffHubTabId;
  label: string;
  path: string;
  icon: ComponentType<{ className?: string }>;
  /** Exact match for the hub root; prefix for the others. */
  exact?: boolean;
}

const TABS: HubTab[] = [
  { id: 'roster', label: 'Roster', path: '/staff', icon: Users, exact: true },
  { id: 'teams', label: 'Teams', path: '/staff/teams', icon: UsersRound },
  { id: 'tickets', label: 'Tickets', path: '/staff/tickets', icon: Trophy },
  { id: 'notes', label: 'Notes', path: '/staff/notes', icon: NotebookPen },
  { id: 'activity', label: 'Activity', path: '/staff/activity', icon: Activity },
  { id: 'migrations', label: 'Migration approvals', path: '/staff/migrations', icon: ArrowRightLeft },
  { id: 'provisioning', label: 'Provisioning approvals', path: '/staff/provisioning', icon: Server },
  { id: 'data-controller', label: 'Data Controller', path: '/staff/data-controller', icon: ShieldCheck },
];

/**
 * `dataController` is decided by `canAccessDataController` (`argus.datacontroller`, or super admin),
 * which is independent of the `argus.staff.*` flags in `StaffPerms`.
 */
export function visibleStaffHubTabs(perms: StaffPerms, dataController = false): HubTab[] {
  return TABS.filter((t) => {
    if (t.id === 'notes') return perms.canReadNotes;
    if (t.id === 'activity' || t.id === 'migrations' || t.id === 'provisioning') return perms.canManage;
    if (t.id === 'data-controller') return dataController;
    return perms.canView;
  });
}

/** Segmented sub-navigation shared by the Staff Manager hub pages (Roster · Teams · Tickets · Notes · Activity). */
export const StaffHubNav = ({ perms, className }: { perms: StaffPerms; className?: string }) => {
  const { pathname } = useLocation();
  const { identity } = useAuth();
  const tabs = visibleStaffHubTabs(perms, canAccessDataController(identity, import.meta.env.DEV));
  if (tabs.length <= 1) return null;
  return (
    <nav className={cn('inline-flex flex-wrap rounded-xl border border-glass-200/20 bg-glass-100/5 p-1 gap-1', className)} aria-label="Staff Manager sections">
      {tabs.map((tab) => {
        const active = tab.exact ? pathname === tab.path : pathname === tab.path || pathname.startsWith(`${tab.path}/`);
        const Icon = tab.icon;
        return (
          <NavLink
            key={tab.id}
            to={tab.path}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'inline-flex items-center gap-2 h-9 px-3.5 rounded-lg text-sm font-semibold transition-colors touch-manipulation',
              active ? 'bg-accent-500/20 text-accent-200 shadow-[inset_0_0_0_1px_rgb(var(--accent-t-400-rgb)/0.35)]' : 'text-glass-400 hover:text-white hover:bg-glass-200/10',
            )}
          >
            <Icon className="w-4 h-4" aria-hidden="true" />
            {tab.label}
          </NavLink>
        );
      })}
    </nav>
  );
};

/** One tile of the bento stat strip under a hub hero. */
export const HubStat = ({ label, value, sub, tone = 'text-accent-300' }: { label: string; value: number | string; sub?: string; tone?: string }) => {
  const shown = useCountUp(typeof value === 'number' ? value : 0);
  return (
    <div className="p-4 md:p-5 bg-[var(--glass-bg)]">
      <div className={cn('text-[11px] font-semibold uppercase tracking-[0.14em]', tone)}>{label}</div>
      <div className={cn('mt-2 font-bold text-white tabular-nums leading-none', typeof value === 'number' ? 'text-3xl' : 'text-2xl')}>
        {typeof value === 'number' ? shown : value}
      </div>
      {sub && <div className="mt-1.5 text-xs text-glass-500">{sub}</div>}
    </div>
  );
};

interface StaffHubHeroProps {
  title: string;
  eyebrow?: string;
  blurb?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  /** Right-aligned controls (refresh, primary action). */
  actions?: ReactNode;
  /** `HubStat` tiles rendered as a strip under the header. */
  stats?: ReactNode;
  /** Sub-navigation rendered under the title. */
  nav?: ReactNode;
}

/** Hero card shared by every Staff Manager hub page: gradient, icon tile, eyebrow, title, blurb, actions, nav, stats. */
export const StaffHubHero = ({ title, eyebrow = 'Staff Management', blurb, icon: Icon = UserCog, actions, stats, nav }: StaffHubHeroProps) => (
  <div className="glass-card relative overflow-hidden">
    <div
      className="absolute inset-0 pointer-events-none bg-[radial-gradient(90%_140%_at_0%_0%,rgb(var(--accent-t-500-rgb)/0.22),transparent_55%),radial-gradient(50%_80%_at_100%_100%,rgb(var(--accent-t-400-rgb)/0.10),transparent_60%)]"
      aria-hidden="true"
    />
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-300/70 to-transparent" aria-hidden="true" />
    <div className="relative p-5 md:p-7 flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-start gap-4 min-w-0">
        <div className="p-3 rounded-2xl bg-accent-500/15 ring-1 ring-accent-400/30 shadow-[0_0_30px_rgb(var(--accent-t-500-rgb)/0.3)] flex-shrink-0">
          <Icon className="w-7 h-7 text-accent-300" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-300/80">{eyebrow}</div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">{title}</h1>
          {blurb && <p className="text-glass-400 mt-1 max-w-2xl">{blurb}</p>}
          {nav && <div className="mt-4">{nav}</div>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
    {stats && <div className="relative border-t border-glass-200/10 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-px bg-glass-200/[0.07]">{stats}</div>}
  </div>
);
